import { useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import api from "../../services/api";
import ModalCustom from "./modal-template-default";
import Slct from "../Slct";
import Required from "../Required";
import DatePicker from "../DatePicker";
import useWrapper from "../../hooks/useWrapper";
import { toast } from "react-toastify";
import Loading from "../Loading";
import Results from "../Results";
import StatusFlag from "../StatusFlag";
import InfoModal from "../InfoModal";
import useFormat from "../../hooks/utils/useFormat";
import { Currency, InputText } from "../Input";
import Button from "../Button";
import { validateForm } from "../ValidateForm";
import usePermission from "../../hooks/usePermission";
import QuotaEditor from "../QuotaEditor";
import Checkbox from "../Checkbox";

const TOLERANCE = 0.01;

/**
 * Registra o distrato (encerramento de vínculo) de um formando: cancela todos
 * os pagamentos em aberto e cobra a diferença entre a multa e o que já foi
 * pago (parcelável) — ou, se já pago mais que a multa, o backend gera
 * reembolso automaticamente em vez de cobrança (sem precisar de nada extra
 * aqui). O aporte para a turma é sempre definido junto.
 */
export default function ModalDistrato(props) {
  const { show, onHide, trainee, refreshCallback, allowDefer = true, defaultDate } = props;

  const { config } = useWrapper();
  const { formatReal } = useFormat();
  const canSubmit = usePermission("trainee", "distrato");

  const [distratoDate, setdistratoDate] = useState(null);
  const [multaValue, setmultaValue] = useState();
  const [aporteValue, setaporteValue] = useState();
  const [description, setdescription] = useState("");
  const [paymentType, setpaymentType] = useState();
  const [paymentMethod, setpaymentMethod] = useState();
  const [quotaConfig, setquotaConfig] = useState({});
  const [quotas, setquotas] = useState([]);
  const [costCenters, setcostCenters] = useState([]);
  const [selects, setselects] = useState({});

  // "lançar a cobrança/reembolso depois": encerra o vínculo agora e deixa o
  // acerto financeiro pendente (formando vai para "Aguardando Distrato")
  const [deferSettlement, setdeferSettlement] = useState(false);

  const [alreadyPaidTotal, setalreadyPaidTotal] = useState(0);
  const [isLoadingStartup, setisLoadingStartup] = useState(true);
  const [isLoadingRequest, setisLoadingRequest] = useState(false);
  const isWaitingResponse = isLoadingStartup || isLoadingRequest;

  const diff = (parseFloat(multaValue) || 0) - alreadyPaidTotal;
  const willCharge = diff > TOLERANCE;
  const willRefund = diff < -TOLERANCE;
  const isExactMatch = !willCharge && !willRefund;

  useEffect(() => {
    if (!show) return;
    (async () => {
      setisLoadingStartup(true);
      try {
        const { data: _paid_slips } = await api.get(
          `paymentslips?trainee_id=${trainee?.id}&status=pago&paginate=false`,
          config,
        );
        // value_paid inclui juros pago — não deve contar como já pago para fins
        // de comparação com a multa (mesmo critério do backend, DistratoService).
        const _total = (_paid_slips || []).reduce(
          (__acc, __slip) => __acc + ((parseFloat(__slip.value_paid) || 0) - (parseFloat(__slip.value_juros) || 0)),
          0,
        );
        setalreadyPaidTotal(_total);

        const { data: _cost_centers } = await api.get("cost-center?type=despesa", config);
        setcostCenters(
          _cost_centers?.map(__cost_center => ({
            value: __cost_center.id,
            label: __cost_center.name,
            expensePlan: __cost_center.expensePlan?.map(__expense_plan => ({
              value: __expense_plan.id,
              label: __expense_plan.name,
              expenses: __expense_plan.expenses?.map(__expense => ({
                value: __expense.id,
                label: __expense.name,
              })),
            })),
          })),
        );
      } catch (__error) {
        toast.error("Erro ao tentar carregar o histórico de pagamentos do formando.");
        console.log("__error", __error);
      } finally {
        setdeferSettlement(false);
        // ao concluir um distrato deixado para depois, a data que vale é a do
        // encerramento do vínculo, não a de hoje
        setdistratoDate(defaultDate ? new Date(defaultDate) : new Date());
        setmultaValue();
        setaporteValue();
        setdescription("");
        setpaymentType();
        setpaymentMethod();
        setselects({});
        setquotaConfig({ quota_value: 1, first_quota_date: null, quota_interval: null, has_entry: false });
        setquotas([]);
        setisLoadingStartup(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, trainee?.id, defaultDate]);

  const closeModal = () => {
    if (isWaitingResponse) return;
    onHide();
  };

  const sendDistrato = async () => {
    if (!canSubmit) return;
    const _is_valid = validateForm(document.getElementById("modal_distrato"));
    if (!_is_valid) return;

    if (deferSettlement) {
      setisLoadingRequest(true);
      try {
        const { data: _trainee } = await api.post(
          "distratos/end-bond",
          { trainee_id: trainee?.id, distrato_date: distratoDate },
          config,
        );
        toast.success("Vínculo encerrado — falta registrar a multa ou o reembolso deste formando.");
        refreshCallback && refreshCallback(null, _trainee);
        onHide();
      } catch (__error) {
        const _message = __error?.response?.data?.error?.message;
        toast.error(_message || "Problemas ao encerrar o vínculo.");
        console.log("__error", __error);
      } finally {
        setisLoadingRequest(false);
      }
      return;
    }

    if ((parseFloat(aporteValue) || 0) > (parseFloat(multaValue) || 0)) {
      toast.error("O valor do aporte não pode ser maior que o valor da multa.");
      return;
    }
    if (!isExactMatch) {
      if (!(quotas?.length > 0)) {
        toast.error(`Configure o parcelamento d${willRefund ? "o reembolso" : "a multa"} antes de salvar.`);
        return;
      }
      if (quotas.some(__quota => !(__quota.value > 0) || !__quota.due_date)) {
        toast.error("Toda parcela precisa de valor e vencimento.");
        return;
      }
      if (!paymentMethod) {
        toast.error(`Selecione o método de pagamento d${willRefund ? "o reembolso" : "a multa"}.`);
        return;
      }
      if (willRefund && !selects?.cost_center_id) {
        toast.error("Selecione o Centro de Custo do reembolso.");
        return;
      }
    }

    setisLoadingRequest(true);
    try {
      const _payload = {
        trainee_id: trainee?.id,
        distrato_date: distratoDate,
        multa_value: parseFloat(multaValue) || 0,
        aporte_value: parseFloat(aporteValue) || 0,
        description,
        ...(!isExactMatch
          ? {
              type_payment: paymentType,
              method_payment: paymentMethod,
              ...(willRefund
                ? {
                    cost_center_id: selects?.cost_center_id,
                    expense_plan_id: selects?.expense_plan_id,
                    expense_id: selects?.expense_id,
                  }
                : {}),
              installments: quotas.map(__quota => ({
                due_date: __quota.due_date,
                value: __quota.value,
                label: __quota.label,
                description: __quota.description || null,
                method_payment: paymentMethod,
              })),
            }
          : { installments: [] }),
      };

      const { data: _distrato } = await api.post("distratos", _payload, config);
      toast.success(
        _distrato?.bill_to_pay_negotiation_id
          ? "Distrato registrado — reembolso gerado ao formando."
          : _distrato?.outcome === "cobranca"
            ? "Distrato registrado — as parcelas da multa estão na aba Distrato."
            : "Distrato registrado — vínculo encerrado, sem cobrança ou reembolso.",
      );
      // o distrato criado vai no callback: quem chama decide o que fazer com ele
      // (a tela do Financeiro Contratado usa para levar o gestor até a aba Distrato)
      refreshCallback && refreshCallback(_distrato);
      onHide();
    } catch (__error) {
      const _message = __error?.response?.data?.error?.message;
      toast.error(_message || "Problemas ao registrar o distrato.");
      console.log("__error", __error);
    } finally {
      setisLoadingRequest(false);
    }
  };

  return (
    <ModalCustom show={show} onHide={closeModal} disableClose={isWaitingResponse} id="modal_distrato" size="lg">
      <ModalCustom.Header>
        <ModalCustom.Title>{allowDefer ? "Encerrar Vínculo (Distrato)" : "Registrar Distrato"}</ModalCustom.Title>
      </ModalCustom.Header>
      <ModalCustom.Body>
        {!isLoadingStartup ? (
          <>
            <span className="mb-4 small-label">
              {deferSettlement ? (
                <>
                  <b>Atenção:</b> ao confirmar, o vínculo será encerrado e todos os pagamentos em aberto deste formando
                  serão cancelados. Só a multa (ou o reembolso) fica para depois — o formando ficará como{" "}
                  <b>Encerrado - Aguardando Distrato</b> até que ela seja registrada. Essa ação não pode ser desfeita.
                </>
              ) : !allowDefer ? (
                // concluindo um distrato adiado: o vínculo já acabou e os boletos
                // já foram cancelados naquele momento — repetir isso aqui faria o
                // gestor achar que vai cancelar pagamento de novo
                <>
                  <b>Atenção:</b> o vínculo deste formando já está encerrado e os pagamentos em aberto já foram
                  cancelados. Ao confirmar, fica registrada a multa (ou o reembolso) e o acesso dele ao sistema passa a
                  ficar limitado até a regularização. Essa ação não pode ser desfeita.
                </>
              ) : (
                <>
                  <b>Atenção:</b> ao confirmar, todos os pagamentos em aberto deste formando serão cancelados e o acesso
                  dele ao sistema ficará limitado (ou encerrado) até a regularização da multa. Essa ação não pode ser
                  desfeita.
                </>
              )}
            </span>
            <div className="divisor mt-2 mb-2"></div>
            {/* sem a classe `im-checkbox`: ela é `position: absolute; right: 0`,
                feita para o canto de um campo (o "WhatsApp?" ao lado do Celular),
                e aqui o checkbox é um bloco próprio — absoluto, ele monta em cima
                do aviso acima */}
            {allowDefer && (
              <span className="d-flex mb-1">
                <label style={{ cursor: "pointer" }} className="d-flex align-items-center">
                  <Checkbox
                    checked={deferSettlement}
                    disabled={isWaitingResponse}
                    onChange={__ev => setdeferSettlement(__ev.target.checked)}
                  />
                  <span className="ml-2">
                    Lançar a cobrança/reembolso depois (encerra o vínculo agora, sem definir a multa)
                  </span>
                </label>
              </span>
            )}
            <Row className="mt-2">
              <Col sm={4}>
                <span>
                  Data do Distrato <Required />
                </span>
                <DatePicker
                  disabled={isWaitingResponse}
                  required={true}
                  selected={distratoDate}
                  dateFormat="dd/MM/yyyy"
                  placeholder="--/--/----"
                  changeDate={__ev => setdistratoDate(__ev)}
                />
              </Col>
              {!deferSettlement && (
                <>
                  <Col sm={4}>
                    <span>Valor da Multa</span>
                    <Currency
                      allowNegative={false}
                      disabled={isWaitingResponse}
                      value={multaValue}
                      valueChange={__ev => {
                        setmultaValue(__ev);
                        setaporteValue();
                      }}
                    />
                  </Col>
                  <Col sm={4}>
                    <span>Valor do Aporte (Turma)</span>
                    <Currency
                      allowNegative={false}
                      disabled={isWaitingResponse}
                      value={aporteValue}
                      isAllowed={__ev => {
                        if (__ev <= (parseFloat(multaValue) || 0)) return true;
                        toast.error("O Aporte não pode ser maior que o valor da Multa.", { toastId: "aporte_max" });
                      }}
                      valueChange={__ev => setaporteValue(__ev)}
                    />
                  </Col>
                </>
              )}
            </Row>
            {!deferSettlement && (
              <Row className="mt-2">
                <Col>
                  <span>
                    Descrição <Required />
                  </span>
                  <InputText
                    required={true}
                    disabled={isWaitingResponse}
                    value={description}
                    onChange={__ev => setdescription(__ev.target.value)}
                    placeholder="Ex: Distrato solicitado pelo formando"
                  />
                </Col>
              </Row>
            )}
            {!deferSettlement && (
              <>
                <Row className="mt-3">
                  <Col lg={6}>
                    {willRefund ? (
                      <p className="small-label mb-0">
                        Este formando já pagou mais do que a multa — em vez de cobrança, será gerado um reembolso e o
                        acesso será encerrado imediatamente.
                      </p>
                    ) : willCharge ? (
                      <p className="small-label mb-0">
                        Será gerada uma cobrança da diferença ao formando, que poderá ser parcelada. O acesso dele
                        ficará limitado a Financeiro e Atendimento até a quitação total da multa, quando então será
                        encerrado automaticamente.
                      </p>
                    ) : (
                      <p className="small-label mb-0">
                        Este formando já pagou exatamente o valor da multa — nenhuma cobrança ou reembolso será gerado.{" "}
                        {allowDefer ? "O vínculo será encerrado e o acesso revogado" : "O acesso será revogado"}{" "}
                        imediatamente.
                      </p>
                    )}
                  </Col>
                  <Col lg={6}>
                    <Results
                      rows={[
                        {
                          label: "Valor da Multa",
                          value: formatReal(parseFloat(multaValue) || 0),
                        },
                        {
                          label: (
                            <>
                              Já Pago pelo Formando{" "}
                              <InfoModal
                                title="Já Pago pelo Formando"
                                body="Não inclui juros/multa por atraso pagos pelo formando — apenas o valor das parcelas."
                              />
                            </>
                          ),
                          value: formatReal(alreadyPaidTotal),
                        },
                        willCharge
                          ? {
                              label: <b>Valor a Cobrar (Multa)</b>,
                              value: <b>{formatReal(diff)}</b>,
                              type: "dark",
                            }
                          : willRefund
                            ? {
                                label: <b>Reembolso ao Formando</b>,
                                value: <b>{formatReal(Math.abs(diff))}</b>,
                                type: "dark",
                              }
                            : {
                                label: <b>Situação</b>,
                                value: <b>Quitado — Nenhum Valor Pendente</b>,
                                type: "dark",
                              },
                      ]}
                    />
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col>
                    <StatusFlag status={willRefund ? "Pagamento" : willCharge ? "Recebimento" : "Quitado"}>
                      {willRefund
                        ? "Pagamento ao Formando (Reembolso)"
                        : willCharge
                          ? "Recebimento do Formando (Cobrança da Multa)"
                          : "Nenhuma Movimentação Financeira"}
                    </StatusFlag>
                  </Col>
                </Row>
                {willRefund && (
                  <Row className="mt-2">
                    <Col sm={4}>
                      <span>
                        Centro de Custo <Required />
                      </span>
                      <Slct
                        required={true}
                        isDisabled={isWaitingResponse}
                        options={costCenters || []}
                        value={selects?.cost_center_id}
                        onChange={__ev => {
                          setselects({
                            ...selects,
                            cost_center_id: __ev.value,
                            expense_plan_id: null,
                            expense_id: null,
                          });
                        }}
                        placeholder="..."
                      />
                    </Col>
                    <Col sm={4}>
                      <span>
                        Plano de Conta <Required />
                      </span>
                      <Slct
                        required={true}
                        isDisabled={isWaitingResponse}
                        options={
                          selects?.cost_center_id
                            ? costCenters?.find(__cost_center => __cost_center.value === selects.cost_center_id)
                                ?.expensePlan || []
                            : []
                        }
                        value={selects?.expense_plan_id}
                        onChange={__ev => {
                          setselects({
                            ...selects,
                            expense_plan_id: __ev.value,
                            expense_id: null,
                          });
                        }}
                        placeholder="..."
                      />
                    </Col>
                    <Col sm={4}>
                      <span>
                        Conta <Required />
                      </span>
                      <Slct
                        required={true}
                        isDisabled={isWaitingResponse}
                        options={
                          selects?.expense_plan_id
                            ? costCenters
                                ?.find(__cost_center => __cost_center.value === selects.cost_center_id)
                                ?.expensePlan?.find(__expense_plan => __expense_plan.value === selects.expense_plan_id)
                                ?.expenses || []
                            : []
                        }
                        value={selects?.expense_id}
                        onChange={__ev => {
                          setselects({
                            ...selects,
                            expense_id: __ev.value,
                          });
                        }}
                        placeholder="..."
                      />
                    </Col>
                  </Row>
                )}
                {!isExactMatch && (
                  <>
                    <Row className="mt-2">
                      <Col sm={4}>
                        <span>
                          Tipo de Pagamento ({willRefund ? "Reembolso" : "Multa"}) <Required />
                        </span>
                        <Slct
                          isDisabled={isWaitingResponse}
                          required={true}
                          options={[
                            { value: "avista", label: "À Vista" },
                            { value: "parcelado", label: "A Prazo / Parcelado" },
                          ]}
                          value={paymentType}
                          onChange={__ev => {
                            setpaymentType(__ev.value);
                            setpaymentMethod(null);
                            setquotaConfig({
                              ...quotaConfig,
                              quota_value: 1,
                              has_entry: false,
                              first_quota_date: __ev.value === "avista" ? new Date() : quotaConfig?.first_quota_date,
                            });
                          }}
                          placeholder="..."
                        />
                      </Col>
                      <Col sm={4}>
                        <span>
                          Método do Pagamento <Required />
                        </span>
                        <Slct
                          isDisabled={isWaitingResponse}
                          required={true}
                          options={
                            willRefund
                              ? [
                                  { value: "pix", label: "Pix" },
                                  { value: "boleto", label: "Boleto" },
                                  { value: "especie", label: "Espécie" },
                                  { value: "cartao", label: "Cartão" },
                                  { value: "transferencia", label: "Transferência" },
                                ]
                              : [
                                  { value: "boleto", label: "Boleto" },
                                  { value: "pix", label: "Pix" },
                                  { value: "especie", label: "Espécie" },
                                  ...(paymentType === "parcelado" ? [{ value: "cartao", label: "Cartão" }] : []),
                                  ...(paymentType === "avista"
                                    ? [{ value: "transferencia", label: "Transferência Bancária" }]
                                    : []),
                                ]
                          }
                          value={paymentMethod}
                          onChange={__ev => setpaymentMethod(__ev.value)}
                          placeholder="..."
                        />
                      </Col>
                    </Row>
                    {!!paymentType && (
                      <QuotaEditor
                        result={willRefund ? Math.abs(diff) : diff}
                        quotas={quotas}
                        setquotas={setquotas}
                        config={quotaConfig}
                        setconfig={setquotaConfig}
                        paymentType={paymentType}
                        disabled={isWaitingResponse}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </>
        ) : (
          <Loading className="mb-2" />
        )}
      </ModalCustom.Body>
      {canSubmit && (
        <ModalCustom.Footer>
          <Button disabled={isWaitingResponse} color="error_ghost" onClick={closeModal}>
            Cancelar
          </Button>
          <Button disabled={isWaitingResponse} loading={isLoadingRequest} color="error" onClick={sendDistrato}>
            {deferSettlement ? "Encerrar Vínculo" : !allowDefer ? "Registrar Distrato" : "Encerrar Vínculo (Distrato)"}
          </Button>
        </ModalCustom.Footer>
      )}
    </ModalCustom>
  );
}
